import { useState, useEffect } from 'react';

const ReasonSettingsTab = ({
    reasonCodes,
    globalReasons,
    onSaveEdits,
    onAddReason,
    onBulkCreate,
    canCreateDefaults,
    onDeleteReason,
    saving,
}) => {
    const [codes, setCodes] = useState([]);
    const [changedIndices, setChangedIndices] = useState([]);

    // Sorted global reasons for category dropdowns
    const sortedGlobal = [...(globalReasons || [])].sort((a, b) =>
        (a.ReasonDescription || '').localeCompare(b.ReasonDescription || '')
    );

    // Sync from props
    useEffect(() => {
        const sorted = [...(reasonCodes || [])].sort(
            (a, b) => parseInt(a.ReasonCodeId) - parseInt(b.ReasonCodeId)
        );
        setCodes(sorted);
        setChangedIndices([]);
    }, [reasonCodes]);

    const updateField = (index, field, value) => {
        setCodes((prev) => {
            const updated = [...prev];
            updated[index] = { ...updated[index], [field]: value };
            return updated;
        });
        if (!changedIndices.includes(index)) {
            setChangedIndices((prev) => [...prev, index]);
        }
    };

    const handleSaveEdits = () => {
        const payload = changedIndices.map((i) => ({
            ReasonCodeId: codes[i].ReasonCodeId,
            Description: codes[i].Description,
            GlobalReasonCode: codes[i].GlobalReasonCode,
        }));
        onSaveEdits(payload);
    };

    const hasDirtyEdits = changedIndices.length > 0;

    return (
        <div className="pp-section">
            <div className="pp-section__header">
                <h3 className="pp-section__title">
                    Reason Codes
                    <span className="pp-section__count">({codes.length})</span>
                </h3>
                <div className="pp-section__actions">
                    {codes.length === 0 && canCreateDefaults ? (
                        <button
                            className="dp-btn dp-btn--secondary dp-btn--sm"
                            disabled={saving}
                            onClick={onBulkCreate}
                        >
                            <i className="fe fe-plus" style={{ fontSize: '13px' }} />
                            Create Defaults
                        </button>
                    ) : null}
                    <button
                        className="dp-btn dp-btn--secondary dp-btn--sm"
                        disabled={saving}
                        onClick={onAddReason}
                    >
                        <i className="fe fe-plus" style={{ fontSize: '13px' }} />
                        Add Reason
                    </button>
                    {hasDirtyEdits ? (
                        <button className="dp-btn dp-btn--primary dp-btn--sm" disabled={saving} onClick={handleSaveEdits}>
                            {saving ? 'Saving...' : 'Save'}
                        </button>
                    ) : null}
                </div>
            </div>
            <div className="pp-section__body">
                {codes.length > 0 ? (
                    <div className="ss-reason-grid">
                        {codes.map((code, i) => (
                            <div key={code.ReasonCodeId || i} className="ss-reason-card">
                                <div className="ss-reason-card__header">
                                    <h4 className="ss-reason-card__title">Reason {i + 1}</h4>
                                    <button
                                        className="ss-reason-card__delete"
                                        title="Delete reason"
                                        onClick={() => onDeleteReason(code.ReasonCodeId, code.Description)}
                                    >
                                        <i className="fe fe-trash-2" />
                                    </button>
                                </div>
                                <div className="ss-reason-card__body">
                                    <div className="ss-reason-card__field">
                                        <label className="ss-reason-card__label">Description</label>
                                        <input
                                            className="ss-reason-card__input"
                                            type="text"
                                            value={code.Description || ''}
                                            onChange={(e) => updateField(i, 'Description', e.target.value)}
                                        />
                                    </div>
                                    <div className="ss-reason-card__field">
                                        <label className="ss-reason-card__label">Category</label>
                                        <select
                                            className="ss-reason-card__select"
                                            value={code.GlobalReasonCode || ''}
                                            onChange={(e) => updateField(i, 'GlobalReasonCode', e.target.value)}
                                        >
                                            {sortedGlobal.map((g) => (
                                                <option key={g.ReasonCodeId} value={g.ReasonCodeId}>
                                                    {g.ReasonDescription}
                                                </option>
                                            ))}
                                        </select>
                                    </div>
                                </div>
                            </div>
                        ))}
                    </div>
                ) : (
                    <div className="ss-empty">
                        <p className="ss-empty__text">
                            {canCreateDefaults
                                ? 'No reason codes configured. Click "Create Defaults" to add standard reason codes or "Add Reason" to create a custom one.'
                                : 'No active reason codes. This community has had reason codes before, so defaults can\'t be recreated here; use "Add Reason" to add each one.'}
                        </p>
                    </div>
                )}
            </div>
        </div>
    );
};

export default ReasonSettingsTab;
